type ModalDimension = {
    element: HTMLElement;
    property: 'width' | 'height';
    variable: string;
};
const geometryTiming: KeyframeAnimationOptions = {
    duration: 260,
    easing: 'cubic-bezier(.32,.72,0,1)',
    fill: 'both'
};

export function observeModalAutoSize(dialog: HTMLDialogElement, content: HTMLElement, axes = { height: true, width: false })
{
    const dimensions: ModalDimension[] = [];

    if (axes.width)
        dimensions.push({ element: dialog, property: 'width', variable: '--ideo-modal-auto-width' });

    if (axes.height)
    {
        dimensions.push({ element: dialog, property: 'height', variable: '--ideo-modal-auto-height' });
        dimensions.push({ element: content, property: 'height', variable: '--ideo-modal-auto-height' });
    }

    const elements = [...new Set(dimensions.map(dimension => dimension.element))];
    const originalSizes = dimensions.map(({ element, variable }) => element.style.getPropertyValue(variable));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const body = content.querySelector<HTMLElement>('[data-modal-body]');
    const fonts = dialog.ownerDocument?.fonts;
    let animations: Animation[] = [];
    let targetSizes: number[] = [];
    let frame: number | null = null;
    let stopped = false;
    let naturalBodyFits = false;

    function cancelAnimations()
    {
        for (const animation of animations)
        {
            animation.onfinish = null;
            animation.cancel();
        }

        animations = [];
        body?.removeAttribute('data-modal-auto-resizing');
    }

    function measureNaturalSizes()
    {
        const effects = animations.map(animation => animation.effect as KeyframeEffect);
        const targets = effects.map(effect => effect.target);
        const sizes = dimensions.map(({ element, variable }) => element.style.getPropertyValue(variable));
        const scrollTop = body?.scrollTop;

        // Własne wymiary i efekty WAAPI nie mogą być wejściem pomiaru, bo uniemożliwią kurczenie
        effects.forEach(effect => effect.target = null);
        dimensions.forEach(({ element, variable }) => element.style.removeProperty(variable));

        try
        {
            // Najpierw szerokość: wysokość musi wynikać z docelowego zawijania, bez skali animacji wejścia
            const measured = readSizes(true);
            naturalBodyFits = !!body && body.scrollHeight <= body.clientHeight;
            return measured;
        }
        finally
        {
            dimensions.forEach(({ element, variable }, index) => element.style.setProperty(variable, sizes[index]));
            effects.forEach((effect, index) => effect.target = targets[index]);

            if (body && scrollTop !== undefined)
                body.scrollTop = scrollTop;
        }
    }

    function readSizes(natural = false)
    {
        return dimensions.map(({ element, property }) =>
        {
            if (property === 'width') return element.offsetWidth;
            if (!natural) return element.offsetHeight;

            // offsetHeight potrafi zaokrąglić wysokość w dół i może ścisnąć body o ułamek piksela
            const style = element.ownerDocument?.defaultView?.getComputedStyle(element);
            const height = Number.parseFloat(style?.height ?? '');
            if (!Number.isFinite(height)) return element.offsetHeight;
            const edges = style?.boxSizing === 'border-box' ? 0 :
                (Number.parseFloat(style?.paddingTop ?? '') || 0)
                + (Number.parseFloat(style?.paddingBottom ?? '') || 0)
                + (Number.parseFloat(style?.borderTopWidth ?? '') || 0)
                + (Number.parseFloat(style?.borderBottomWidth ?? '') || 0);
            return Math.max(element.offsetHeight, Math.ceil(height + edges));
        });
    }

    function keyframe(element: HTMLElement, sizes: number[]): Keyframe
    {
        const frame: Keyframe = {};

        dimensions.forEach((dimension, index) =>
        {
            if (dimension.element === element)
                frame[dimension.property] = `${sizes[index]}px`;
        });

        return frame;
    }

    function syncBodyOverflow()
    {
        // Ukrywamy tylko scroll wywołany przejściowym ściskaniem, nie ograniczeniem viewportu
        if (axes.height && naturalBodyFits && animations.length)
            body?.setAttribute('data-modal-auto-resizing', '');
        else
            body?.removeAttribute('data-modal-auto-resizing');
    }

    function resize(animate = true)
    {
        if (stopped || !dialog.open || !dialog.isConnected)
            return;

        const previous = readSizes();
        const next = measureNaturalSizes();

        if (next.every((size, index) => size === targetSizes[index]))
        {
            syncBodyOverflow();
            return;
        }

        cancelAnimations();
        targetSizes = next;
        dimensions.forEach(({ element, variable }, index) => element.style.setProperty(variable, `${next[index]}px`));

        if (!animate || motion.matches || typeof dialog.animate !== 'function')
            return;

        animations = elements.map(element => element.animate([
            keyframe(element, previous),
            keyframe(element, next)
        ], geometryTiming));
        syncBodyOverflow();

        for (const animation of animations)
        {
            animation.onfinish = () =>
            {
                animation.onfinish = null;
                animation.cancel();
                animations = animations.filter(current => current !== animation);
                syncBodyOverflow();
            };
        }
    }

    function refresh()
    {
        if (stopped || frame !== null)
            return;

        frame = requestAnimationFrame(() =>
        {
            frame = null;
            resize();
        });
    }

    const observer = new ResizeObserver(refresh);
    const observed = new Set<Element>();

    function observeContent()
    {
        const next = new Set<Element>([content, ...content.children, ...(body?.children ?? [])]);

        for (const element of observed)
        {
            if (!next.has(element))
            {
                observer.unobserve(element);
                observed.delete(element);
            }
        }

        for (const element of next)
        {
            if (!observed.has(element))
            {
                observer.observe(element);
                observed.add(element);
            }
        }
    }

    const mutations = new MutationObserver((records) =>
    {
        // Zapis własnej geometrii nie jest zmianą treści i nie może podtrzymywać pętli RAF
        const changes = records.filter(record => record.target !== content || record.attributeName !== 'style');

        if (!changes.length)
            return;

        if (changes.some(record => record.type === 'childList'))
            observeContent();

        refresh();
    });

    function onMotionChange()
    {
        if (motion.matches)
            cancelAnimations();

        resize(false);
    }

    resize(false);
    observeContent();
    mutations.observe(content, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden']
    });
    content.addEventListener('load', refresh, true);
    fonts?.addEventListener('loadingdone', refresh);
    void fonts?.ready.then(refresh);
    motion.addEventListener('change', onMotionChange);
    window.addEventListener('resize', refresh);

    return {
        refresh,
        stop()
        {
            stopped = true;
            observer.disconnect();
            mutations.disconnect();
            content.removeEventListener('load', refresh, true);
            fonts?.removeEventListener('loadingdone', refresh);
            motion.removeEventListener('change', onMotionChange);
            window.removeEventListener('resize', refresh);

            if (frame !== null)
                cancelAnimationFrame(frame);

            frame = null;
            cancelAnimations();
            dimensions.forEach(({ element, variable }, index) =>
            {
                if (originalSizes[index])
                    element.style.setProperty(variable, originalSizes[index]);
                else
                    element.style.removeProperty(variable);
            });
        }
    };
}
